import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const STORAGE_KEY = 'ptv_app_banner_dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24h
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=cl.programatuviaje.app';
const APP_STORE_URL = 'https://apps.apple.com/co/app/programatuviaje-cl/id6761294424';

function getMobilePlatform(): 'android' | 'ios' | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return null;
}

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return Date.now() - ts < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

export function SmartAppBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'android' | 'ios' | null>(null);

  useEffect(() => {
    const p = getMobilePlatform();
    if (p && !isDismissed()) {
      setPlatform(p);
      setVisible(true);
    }
  }, []);

  function dismiss() {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  }

  if (!visible || !platform) return null;

  const isAndroid = platform === 'android';

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 md:hidden">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl flex items-center gap-3 px-4 py-3">
        {/* App icon */}
        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
          <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9,19h8.5a3.5,3.5 0,0 0,0 -7h-11a3.5,3.5 0,0 1,0 -7H15" />
            <path d="M6,22a3,3 0,1 1,0 -6a3,3 0,1 1,0 6" />
            <path d="M18,8a3,3 0,1 1,0 -6a3,3 0,1 1,0 6" />
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-900 leading-tight">ProgramaTuViaje</p>
          <p className="text-xs text-gray-500 leading-tight truncate">Mejor experiencia en la app</p>
        </div>

        {/* CTA */}
        {isAndroid ? (
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            className="shrink-0 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Abrir en Play
          </a>
        ) : (
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            className="shrink-0 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
          >
            App Store
          </a>
        )}

        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400"
          aria-label="Cerrar"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
